import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { LogOut, ListChecks, Loader2, AlertTriangle, Bot, Check, Lock, Crown } from "lucide-react";
import { useDive } from "../context/DiveContext";
import { DownloadReportButton } from "../lib/useDownloadReport";
import { api } from "../lib/api";

const CATEGORIES = ["Equity", "Mutual Funds", "Bonds", "Gold/Silver", "REIT/InvIT", "ETF", "FD/RD", "PF", "Insurance", "Crypto"];
const RISK = ["Conservative", "Balanced", "Aggressive"];
const RETURN = ["Modest", "Moderate", "High"];
const DIV = ["Low", "Medium", "High"];

function errorMessageOf(err, fallback) {
  const data = err?.response?.data;
  return data?.message || data?.issues?.[0]?.message || fallback;
}

export default function Preferences() {
  const { user, prefs, savePrefs, logout, setScreen, deleteAccount, updateProfile, changePassword } = useDive();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const [prefsSaveError, setPrefsSaveError] = useState("");
  const [dataExportLoading, setDataExportLoading] = useState(false);
  const [dataExportRequested, setDataExportRequested] = useState(false);
  const [dataExportError, setDataExportError] = useState("");

  async function requestDataExport() {
    setDataExportLoading(true);
    setDataExportError("");
    try {
      await api.post("/users/me/data-export-request");
      setDataExportRequested(true);
    } catch (err) {
      setDataExportError(errorMessageOf(err, "Couldn't submit your request. Please try again."));
    } finally {
      setDataExportLoading(false);
    }
  }

  const [name, setName] = useState(user?.name || "");
  const [age, setAge] = useState(user ? String(user.age) : "");
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState("");
  const [profileSaved, setProfileSaved] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");

  if (!user) return null;

  const saveProfile = async () => {
    setProfileSaving(true);
    setProfileError("");
    setProfileSaved(false);
    try {
      await updateProfile({ name, age: Number(age) });
      setProfileSaved(true);
    } catch (err) {
      setProfileError(errorMessageOf(err, "Couldn't save your profile. Please try again."));
    } finally {
      setProfileSaving(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    setPasswordSaving(true);
    setPasswordError("");
    setPasswordSuccess("");
    try {
      const message = await changePassword({ currentPassword, newPassword, confirmNewPassword });
      setPasswordSuccess(message || "Password updated.");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmNewPassword("");
    } catch (err) {
      setPasswordError(errorMessageOf(err, "Couldn't change your password. Please try again."));
    } finally {
      setPasswordSaving(false);
    }
  };

  // savePrefs applies optimistically and rolls itself back on failure — the
  // rollback is silent from the UI's perspective unless we surface it here,
  // otherwise a failed save looks identical to a successful one.
  const handleSavePrefs = async (next) => {
    const ok = await savePrefs(next);
    setPrefsSaveError(ok ? "" : "Couldn't save that change — check your connection and try again.");
  };

  const confirmDeleteAccount = async () => {
    setDeleting(true);
    setError("");
    try {
      await deleteAccount();
    } catch (err) {
      setError("Couldn't delete your account. Please try again.");
      setDeleting(false);
    }
  };
  const toggleChip = (list, key, val) => {
    const has = list.includes(val);
    handleSavePrefs({ ...prefs, [key]: has ? list.filter((x) => x !== val) : [...list, val] });
  };
  const customized = prefs.risk !== "Balanced" || prefs.excluded.length > 0;

  return (
    <div className="min-h-full dive-app-surface pb-10" data-testid="preferences-screen">
      <div className="px-6 pt-8 flex items-center justify-between gap-4">
        <div>
          <h1 className="font-heading font-black text-2xl">{user.name}</h1>
          <p className="text-xs text-[var(--text-secondary)] mt-0.5">{user.email}</p>
          <span className={`inline-block mt-1 text-xs font-bold px-2 py-0.5 rounded-md ${customized ? "bg-[var(--dive-blue-light)] text-[var(--dive-blue-dark)]" : "bg-[#D1FAE5] text-[#047857]"}`}>
            {customized ? "Customized" : "Security-first (default)"}
          </span>
        </div>
        <button data-testid="logout-btn" onClick={logout} className="w-10 h-10 rounded-xl bg-[var(--surface-card)] border border-[var(--border)] flex items-center justify-center shrink-0">
          <LogOut size={18} className="text-[var(--text-secondary)]" />
        </button>
      </div>

      {prefsSaveError && (
        <div className="px-6 mt-4">
          <p className="text-xs text-[var(--red)] font-semibold" data-testid="prefs-save-error">{prefsSaveError}</p>
        </div>
      )}

      <Section title="Personal info">
        <label className="text-xs font-bold uppercase tracking-widest text-[var(--text-tertiary)] mb-2 block">Name</label>
        <input data-testid="profile-name-input" value={name} onChange={(e) => { setName(e.target.value); setProfileSaved(false); }}
          className="w-full rounded-xl border border-[var(--border)] bg-[var(--surface-card)] px-4 py-3 mb-4 outline-none font-semibold text-[var(--text-primary)]" />
        <label className="text-xs font-bold uppercase tracking-widest text-[var(--text-tertiary)] mb-2 block">Age</label>
        <input data-testid="profile-age-input" type="number" min="18" value={age} onChange={(e) => { setAge(e.target.value); setProfileSaved(false); }}
          className="w-full rounded-xl border border-[var(--border)] bg-[var(--surface-card)] px-4 py-3 mb-4 outline-none font-semibold text-[var(--text-primary)]" />
        {profileError && <p className="text-xs text-[var(--red)] font-semibold mb-3" data-testid="profile-save-error">{profileError}</p>}
        <button data-testid="profile-save-btn" onClick={saveProfile} disabled={profileSaving || !name.trim() || !age}
          className="w-full md:max-w-xs md:ml-auto gold-btn rounded-full py-3.5 font-bold flex items-center justify-center gap-2 disabled:opacity-40 transition-colors">
          {profileSaving ? <Loader2 size={16} className="animate-spin" /> : profileSaved ? <Check size={16} /> : null}
          {profileSaving ? "Saving…" : profileSaved ? "Saved" : "Save changes"}
        </button>
      </Section>

      <Section title="Risk appetite">
        <SegRow options={RISK} value={prefs.risk} onChange={(v) => handleSavePrefs({ ...prefs, risk: v })} testid="risk" />
      </Section>
      <Section title="Return expectation">
        <SegRow options={RETURN} value={prefs.returnExpectation} onChange={(v) => handleSavePrefs({ ...prefs, returnExpectation: v })} testid="return" />
      </Section>
      <Section title="Diversification priority" hint="How hard should DIVVE push you to spread out?">
        <SegRow options={DIV} value={prefs.diversificationPriority} onChange={(v) => handleSavePrefs({ ...prefs, diversificationPriority: v })} testid="div" />
      </Section>

      <Section title="Preferred categories">
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <Chip key={c} label={c} active={prefs.preferred.includes(c)} onClick={() => toggleChip(prefs.preferred, "preferred", c)} testid={`pref-${c}`} />
          ))}
        </div>
      </Section>
      <Section title="Excluded categories" hint="Things you never want DIVVE to suggest.">
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <Chip key={c} label={c} active={prefs.excluded.includes(c)} danger onClick={() => toggleChip(prefs.excluded, "excluded", c)} testid={`excl-${c}`} />
          ))}
        </div>
      </Section>

      <div className="px-6 mt-4">
        <p className="text-xs text-[var(--text-secondary)] text-center">Change these anytime — DIVVE adapts instantly.</p>
      </div>

      <NotificationPrefsSection />

      <Section title="Reports" hint="Your full resilience score breakdown, as a downloadable one-pager.">
        <DownloadReportButton testId="prefs-download-report-btn" />
      </Section>

      <div className="px-6 mt-6">
        <button data-testid="prefs-manage-holdings-btn" onClick={() => setScreen("myHoldings")}
          className="w-full md:max-w-xs md:mx-auto bg-[var(--surface-card)] border border-[var(--border)] rounded-full py-3.5 font-bold flex items-center justify-center gap-2 hover:bg-[var(--surface-card-hover)] transition-colors">
          <ListChecks size={18} className="text-[var(--dive-blue)]" /> Manage holdings
        </button>
      </div>

      <div className="px-6 mt-3">
        <button data-testid="prefs-manage-subscription-btn" onClick={() => setScreen("subscription")}
          className="w-full md:max-w-xs md:mx-auto bg-[var(--surface-card)] border border-[var(--border)] rounded-full py-3.5 font-bold flex items-center justify-center gap-2 hover:bg-[var(--surface-card-hover)] transition-colors">
          <Crown size={18} className="text-[var(--dive-blue)]" /> Manage subscription
        </button>
      </div>

      <div className="px-6 mt-3">
        <button data-testid="prefs-simulate-popup-btn" onClick={() => setScreen("divebot")}
          className="w-full md:max-w-xs md:mx-auto bg-[var(--surface-card)] border border-[var(--border)] rounded-full py-3.5 font-bold flex items-center justify-center gap-2 hover:bg-[var(--surface-card-hover)] transition-colors">
          <Bot size={18} className="text-[var(--dive-blue)]" /> Simulate app pop-up
        </button>
      </div>

      <Section title="Change password">
        <form onSubmit={savePassword}>
          <label className="text-xs font-bold uppercase tracking-widest text-[var(--text-tertiary)] mb-2 block">Current password</label>
          <input data-testid="password-current-input" type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required
            className="w-full rounded-xl border border-[var(--border)] bg-[var(--surface-card)] px-4 py-3 mb-4 outline-none font-semibold text-[var(--text-primary)]" />
          <label className="text-xs font-bold uppercase tracking-widest text-[var(--text-tertiary)] mb-2 block">New password</label>
          <input data-testid="password-new-input" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required
            className="w-full rounded-xl border border-[var(--border)] bg-[var(--surface-card)] px-4 py-3 mb-4 outline-none font-semibold text-[var(--text-primary)]" />
          <label className="text-xs font-bold uppercase tracking-widest text-[var(--text-tertiary)] mb-2 block">Confirm new password</label>
          <input data-testid="password-confirm-input" type="password" value={confirmNewPassword} onChange={(e) => setConfirmNewPassword(e.target.value)} required
            className="w-full rounded-xl border border-[var(--border)] bg-[var(--surface-card)] px-4 py-3 mb-4 outline-none font-semibold text-[var(--text-primary)]" />
          {passwordError && <p className="text-xs text-[var(--red)] font-semibold mb-3" data-testid="password-save-error">{passwordError}</p>}
          {passwordSuccess && <p className="text-xs text-[var(--dive-blue)] font-semibold mb-3" data-testid="password-save-success">{passwordSuccess}</p>}
          <button data-testid="password-save-btn" type="submit" disabled={passwordSaving}
            className="w-full md:max-w-xs md:ml-auto gold-btn rounded-full py-3.5 font-bold flex items-center justify-center gap-2 disabled:opacity-40 transition-colors">
            {passwordSaving ? <Loader2 size={16} className="animate-spin" /> : null}
            {passwordSaving ? "Updating…" : "Update password"}
          </button>
        </form>
      </Section>

      <div className="px-6 mt-8">
        <h2 className="font-heading font-bold text-lg">Your data</h2>
        <p className="text-xs text-[var(--text-secondary)] mb-3">Request a copy of everything Divve holds about you. We'll review and get back to you.</p>
        {dataExportError && <p className="text-xs text-[var(--red)] font-semibold mb-3">{dataExportError}</p>}
        {dataExportRequested ? (
          <p className="text-xs text-[var(--dive-blue)] font-semibold" data-testid="data-export-requested-note">Request received — we'll email you once it's ready.</p>
        ) : (
          <button
            data-testid="request-data-export-btn"
            onClick={requestDataExport}
            disabled={dataExportLoading}
            className="block w-full md:max-w-xs rounded-full py-3.5 font-bold border border-[var(--border)] hover:bg-[var(--surface-card-hover)] transition-colors disabled:opacity-50"
          >
            {dataExportLoading ? "Requesting…" : "Request my data export"}
          </button>
        )}
      </div>

      <div className="px-6 mt-8">
        <h2 className="font-heading font-bold text-lg text-[var(--red)]">Danger zone</h2>
        <p className="text-xs text-[var(--text-secondary)] mb-3">Permanently deletes your account and every holding you've added. This can't be undone.</p>
        {error && <p className="text-xs text-[var(--red)] font-semibold mb-3">{error}</p>}
        {!confirmingDelete ? (
          <button data-testid="delete-account-btn" onClick={() => setConfirmingDelete(true)}
            className="block w-full md:max-w-xs md:mx-auto rounded-full py-3.5 font-bold border border-[var(--red)] text-[var(--red)] hover:bg-[var(--red)]/10 transition-colors">
            Delete account
          </button>
        ) : (
          <div className="bg-[var(--red)]/10 border border-[var(--red)]/20 rounded-2xl p-4" data-testid="delete-account-confirm">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle size={16} className="text-[var(--red)]" />
              <span className="text-sm font-bold text-[var(--red)]">Are you sure?</span>
            </div>
            <p className="text-xs text-[var(--text-secondary)] mb-4">All your holdings and account data will be permanently deleted.</p>
            <div className="flex gap-3">
              <button data-testid="delete-account-cancel-btn" onClick={() => setConfirmingDelete(false)} disabled={deleting}
                className="flex-1 rounded-full py-3 font-bold bg-[var(--surface-card)] border border-[var(--border)]">
                Cancel
              </button>
              <button data-testid="delete-account-confirm-btn" onClick={confirmDeleteAccount} disabled={deleting}
                className="flex-1 rounded-full py-3 font-bold bg-[var(--red)] text-white flex items-center justify-center gap-2 disabled:opacity-60">
                {deleting ? <Loader2 size={16} className="animate-spin" /> : null} Yes, delete everything
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Notification preferences (Phase 5 of docs/ADMIN_PANEL_PLAN.md §7) — a
// category with userOptOutAllowed:false (account/subscription notices)
// shows locked, always-on toggles rather than hiding the row entirely, so
// it's clear those categories exist and why they can't be turned off.
function NotificationPrefsSection() {
  const [categories, setCategories] = useState(null);
  const [error, setError] = useState("");

  async function load() {
    try {
      const { data } = await api.get("/notifications/preferences");
      setCategories(data.preferences);
    } catch {
      setError("Couldn't load notification preferences.");
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function toggle(categoryKey, channel, enabled) {
    setCategories((list) =>
      list.map((c) => (c.categoryKey === categoryKey ? { ...c, channels: c.channels.map((ch) => (ch.channel === channel ? { ...ch, enabled } : ch)) } : c))
    );
    try {
      await api.patch("/notifications/preferences", { categoryKey, channel, enabled });
    } catch {
      setError("Couldn't save that change — check your connection and try again.");
      await load(); // revert to the real server state
    }
  }

  if (!categories) return null;

  return (
    <Section title="Notification preferences">
      {error && <p className="text-xs text-[var(--red)] font-semibold mb-3" data-testid="notification-prefs-error">{error}</p>}
      <div className="space-y-3">
        {categories.map((c) => (
          <div key={c.categoryKey} className="flex items-center justify-between rounded-xl border border-[var(--border)] bg-[var(--surface-card)] px-4 py-3" data-testid={`notification-pref-row-${c.categoryKey}`}>
            <div className="flex items-center gap-2">
              {!c.userOptOutAllowed && <Lock size={13} className="text-[var(--text-tertiary)]" />}
              <span className="text-sm font-semibold">{c.label}</span>
            </div>
            <div className="flex items-center gap-4">
              {c.channels.map((ch) => (
                <label key={ch.channel} className="flex items-center gap-1.5 text-xs font-bold text-[var(--text-tertiary)] capitalize">
                  <input
                    type="checkbox"
                    data-testid={`notification-pref-${c.categoryKey}-${ch.channel}`}
                    checked={ch.enabled}
                    disabled={!c.userOptOutAllowed}
                    onChange={(e) => toggle(c.categoryKey, ch.channel, e.target.checked)}
                  />
                  {ch.channel === "in_app" ? "In-app" : ch.channel === "popup" ? "Pop-up" : "Email"}
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

function Section({ title, hint, children }) {
  return (
    <div className="px-6 mt-6">
      <h2 className="font-heading font-bold text-lg">{title}</h2>
      {hint && <p className="text-xs text-[var(--text-secondary)] mb-3">{hint}</p>}
      <div className={hint ? "" : "mt-3"}>{children}</div>
    </div>
  );
}

function SegRow({ options, value, onChange, testid }) {
  return (
    <div className="flex gap-1 bg-[var(--surface-card)] rounded-full p-1 border border-[var(--border)]">
      {options.map((o) => (
        <button key={o} data-testid={`${testid}-${o}`} onClick={() => onChange(o)}
          className={`flex-1 rounded-full py-2 text-[11px] sm:text-xs font-bold transition-colors ${value === o ? "gold-btn" : "text-[var(--text-secondary)]"}`}>
          {o}
        </button>
      ))}
    </div>
  );
}

function Chip({ label, active, danger, onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`px-3 py-2 rounded-full text-sm font-semibold border transition-colors ${active
        ? danger ? "bg-[#FEE2E2] border-[var(--red)] text-[#B91C1C]" : "bg-[var(--dive-blue-light)] border-[var(--dive-blue)] text-[var(--dive-blue-dark)]"
        : "bg-[var(--surface-card)] border-[var(--border)] text-[var(--text-secondary)]"}`}>
      {label}
    </button>
  );
}
